import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Catches uncaught render/effect throws so the task pane shows a recoverable
 * error instead of silently blanking. In an Office task pane a thrown error
 * unmounts the whole React tree, leaving #root empty — which reads to the user
 * as "the page refreshed and everything disappeared." This boundary turns that
 * into a visible, retryable state and logs the real error for diagnosis.
 */
interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught error in task pane:', error, info.componentStack);
  }

  private handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 bg-background px-6 text-center">
        <h2 className="text-base font-semibold text-foreground">Something went wrong</h2>
        <p className="max-w-xs text-sm text-muted-foreground">
          The panel hit an unexpected error. Your document is unaffected — try again.
        </p>
        <p className="max-w-xs break-words text-xs text-muted-foreground/70">{error.message}</p>
        <button
          type="button"
          onClick={this.handleReset}
          className="mt-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Try again
        </button>
      </div>
    );
  }
}
